/**
 * ShopScene V2 — sklep z karuzelą wieszaków (wspólny rackView),
 * przymierzalnia z kotarą, kasa (paragon), manekin-pies (PLAN-V2 §6 Shop).
 */
import Phaser from 'phaser';
import { ITEMS, type ItemDef } from '@pieskowo/shared';
import { BaseScene } from '../../core/BaseScene.js';
import { UiKit, MIN_TOUCH } from '../../core/UiKit.js';
import { getSceneRouter } from '../../core/SceneRouter.js';
import {
  AnimatedDogAvatar,
  OutfitStore,
  postPurchase,
  fetchMe,
  newIdempotencyKey,
  itemTextureKey,
  emptyOutfitSlots,
  emptySwimSlots,
  type OutfitSlots,
  type SwimSlots,
} from '../../avatar/index.js';
import {
  ObjectLayer,
  playFx,
  type InteractEvent,
  type InteractTarget,
  type ObjectDef,
  type SpawnPlacement,
} from '../../interact/index.js';
import {
  RackView,
  applyEquip,
  hitDogForEquip,
  itemIdFromRackDef,
  shopCatalog,
  type RackTabId,
  type ShopFilter,
} from '../wardrobe/rackView.js';
import {
  createOutfitPersist,
  disposeOutfitPersist,
  type OutfitPersistController,
} from '../wardrobe/outfitPersist.js';
import {
  INTERACT_BUS_EVENT,
  shopMissionFromTryOn,
} from './missionHook.js';

const SHOP_TEX_SIZE = 160;

type ShopDecorKind =
  | 'curtain'
  | 'register'
  | 'bell'
  | 'bag'
  | 'mirror'
  | 'hanger_stand';

/** Mapa texture key → rodzaj rysunku proceduralnego. */
export const SHOP_TEXTURE_SHAPES: Readonly<Record<string, ShopDecorKind>> = {
  obj_shop_curtain: 'curtain',
  obj_shop_register: 'register',
  obj_shop_bell: 'bell',
  obj_shop_bag: 'bag',
  obj_shop_mirror: 'mirror',
  obj_shop_hanger_stand: 'hanger_stand',
};

/**
 * Upewnij się, że wszystkie tekstury sklepu istnieją.
 * PNG z Preload mają pierwszeństwo; brak → narysuj proceduralnie (bez emoji).
 */
export function ensureShopTextures(scene: Phaser.Scene): void {
  for (const def of SHOP_OBJECT_DEFS) {
    ensureShopTexture(scene, def.texture);
  }
}

export function ensureShopTexture(
  scene: Phaser.Scene,
  textureKey: string,
): string {
  if (scene.textures.exists(textureKey)) return textureKey;
  const kind = SHOP_TEXTURE_SHAPES[textureKey];
  if (!kind) return textureKey;

  const g = scene.make.graphics({ x: 0, y: 0 });
  drawShopShape(g, kind, SHOP_TEX_SIZE, SHOP_TEX_SIZE);
  g.generateTexture(textureKey, SHOP_TEX_SIZE, SHOP_TEX_SIZE);
  g.destroy();
  return textureKey;
}

export function shopShapeForTexture(
  textureKey: string,
): ShopDecorKind | null {
  return SHOP_TEXTURE_SHAPES[textureKey] ?? null;
}

export function drawShopShape(
  g: Phaser.GameObjects.Graphics,
  kind: ShopDecorKind,
  w: number,
  h: number,
): void {
  g.clear();
  const cx = w / 2;
  const cy = h / 2;
  switch (kind) {
    case 'curtain':
      drawCurtain(g, cx, cy);
      break;
    case 'register':
      drawRegister(g, cx, cy);
      break;
    case 'bell':
      drawBell(g, cx, cy);
      break;
    case 'bag':
      drawBag(g, cx, cy);
      break;
    case 'mirror':
      drawMirror(g, cx, cy);
      break;
    case 'hanger_stand':
      drawHangerStand(g, cx, cy);
      break;
  }
}

function drawCurtain(
  g: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
): void {
  // karnisz
  g.fillStyle(0xd4a574, 1);
  g.fillRoundedRect(cx - 58, cy - 58, 116, 14, 6);
  // tkanina
  g.fillStyle(0xff9ec8, 1);
  g.fillRoundedRect(cx - 50, cy - 48, 100, 100, 8);
  // fałdy
  g.fillStyle(0xffb6c1, 0.85);
  for (let i = 0; i < 5; i++) {
    const x = cx - 40 + i * 18;
    g.fillEllipse(x, cy + 10, 14, 90);
  }
  g.fillStyle(0xffffff, 0.35);
  g.fillRect(cx - 50, cy - 48, 100, 10);
}

function drawRegister(
  g: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
): void {
  // korpus
  g.fillStyle(0xb0bec5, 1);
  g.fillRoundedRect(cx - 48, cy - 20, 96, 60, 10);
  // ekran
  g.fillStyle(0x81c784, 1);
  g.fillRoundedRect(cx - 36, cy - 48, 72, 32, 6);
  g.fillStyle(0xffffff, 0.5);
  g.fillRect(cx - 28, cy - 40, 40, 8);
  // przyciski
  g.fillStyle(0xffcc80, 1);
  for (let r = 0; r < 2; r++) {
    for (let c = 0; c < 3; c++) {
      g.fillCircle(cx - 24 + c * 24, cy + 4 + r * 18, 7);
    }
  }
  // szuflada
  g.fillStyle(0x90a4ae, 1);
  g.fillRoundedRect(cx - 40, cy + 28, 80, 14, 4);
}

function drawBell(
  g: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
): void {
  // dzwonek hotelowy
  g.fillStyle(0xffd54f, 1);
  g.fillEllipse(cx, cy + 8, 90, 28);
  g.fillStyle(0xffc107, 1);
  g.fillEllipse(cx, cy - 8, 70, 50);
  g.fillStyle(0xffecb3, 0.9);
  g.fillEllipse(cx - 10, cy - 18, 28, 18);
  // przycisk
  g.fillStyle(0xef5350, 1);
  g.fillCircle(cx, cy - 36, 12);
  g.fillStyle(0xffffff, 0.5);
  g.fillCircle(cx - 3, cy - 39, 4);
}

function drawBag(
  g: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
): void {
  // torba
  g.fillStyle(0xffb74d, 1);
  g.fillRoundedRect(cx - 40, cy - 20, 80, 70, 10);
  g.fillStyle(0xffe0b2, 0.7);
  g.fillRoundedRect(cx - 32, cy - 8, 64, 48, 8);
  // uszka (łuki z linii — bez Phaser.Math w runtime testów)
  g.lineStyle(6, 0xff8a65, 1);
  g.lineBetween(cx - 28, cy - 18, cx - 18, cy - 42);
  g.lineBetween(cx - 18, cy - 42, cx - 6, cy - 18);
  g.lineBetween(cx + 6, cy - 18, cx + 18, cy - 42);
  g.lineBetween(cx + 18, cy - 42, cx + 28, cy - 18);
  // serduszko
  g.fillStyle(0xff6b6b, 1);
  g.fillCircle(cx - 8, cy + 12, 8);
  g.fillCircle(cx + 8, cy + 12, 8);
  g.fillTriangle(cx - 14, cy + 14, cx + 14, cy + 14, cx, cy + 30);
}

function drawMirror(
  g: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
): void {
  // rama
  g.fillStyle(0xffd54f, 1);
  g.fillEllipse(cx, cy, 100, 120);
  // szkło
  g.fillStyle(0xb3e5fc, 1);
  g.fillEllipse(cx, cy, 78, 96);
  g.fillStyle(0xe1f5fe, 0.7);
  g.fillEllipse(cx - 12, cy - 16, 30, 40);
  // nóżka
  g.fillStyle(0xd4a574, 1);
  g.fillRoundedRect(cx - 8, cy + 52, 16, 18, 4);
  g.fillEllipse(cx, cy + 68, 40, 12);
}

function drawHangerStand(
  g: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
): void {
  // stojak
  g.lineStyle(8, 0xa1887f, 1);
  g.lineBetween(cx, cy + 52, cx, cy - 40);
  g.lineStyle(7, 0x8d6e63, 1);
  g.lineBetween(cx - 44, cy - 40, cx + 44, cy - 40);
  // podstawa
  g.fillStyle(0xbcaaa4, 1);
  g.fillEllipse(cx, cy + 56, 70, 16);
  // wieszaki + ubranka
  const colors = [0xff9ec8, 0x7ec8e3, 0xb8e6d0];
  for (let i = 0; i < 3; i++) {
    const x = cx - 28 + i * 28;
    g.lineStyle(3, 0xb0bec5, 1);
    g.lineBetween(x, cy - 40, x, cy - 20);
    g.fillStyle(colors[i]!, 1);
    g.fillRoundedRect(x - 14, cy - 18, 28, 36, 6);
    g.fillStyle(0xffffff, 0.35);
    g.fillRect(x - 10, cy - 12, 20, 6);
  }
}

/** Klucz tekstury pieska-sprzedawcy (dog_*_idle), z fallbackiem. */
export function sellerDogTextureKey(
  scene: Phaser.Scene,
  breed: string,
): string | null {
  const primary = `dog_${breed}_idle`;
  if (scene.textures.exists(primary)) return primary;
  if (scene.textures.exists('dog_maltese_idle')) return 'dog_maltese_idle';
  // Preload ładuje też maltese pod aliasem
  if (scene.textures.exists('dog_corgi_idle')) return 'dog_corgi_idle';
  return null;
}

/** Definicje obiektów sklepu — ≥6. Tekstury proceduralne/PNG (nie placeholdery emoji). */
export const SHOP_OBJECT_DEFS: ObjectDef[] = [
  {
    id: 'obj_shop_curtain',
    texture: 'obj_shop_curtain',
    size: 96,
    interactions: ['toggle', 'tap'],
    states: ['open', 'closed'],
    fx: 'sparkle',
  },
  {
    id: 'obj_shop_register',
    texture: 'obj_shop_register',
    size: 80,
    interactions: ['tap'],
    fx: 'notes',
  },
  {
    id: 'obj_shop_bell',
    texture: 'obj_shop_bell',
    size: 64,
    interactions: ['tap'],
    fx: 'notes',
  },
  {
    id: 'obj_shop_bag',
    texture: 'obj_shop_bag',
    size: 64,
    interactions: ['drag', 'pickup'],
    fx: 'stars',
  },
  {
    id: 'obj_shop_mirror',
    texture: 'obj_shop_mirror',
    size: 72,
    interactions: ['tap'],
    fx: 'sparkle',
    dogReaction: 'happy',
  },
  {
    id: 'obj_shop_hanger_stand',
    texture: 'obj_shop_hanger_stand',
    size: 70,
    interactions: ['tap'],
    fx: 'sparkle',
    dogReaction: 'sniff',
  },
];

const SHOP_PLACEMENTS: SpawnPlacement[] = [
  { defId: 'obj_shop_curtain', x: 200, y: 340 },
  { defId: 'obj_shop_register', x: 980, y: 420 },
  { defId: 'obj_shop_bell', x: 1040, y: 360 },
  { defId: 'obj_shop_bag', x: 900, y: 500 },
  { defId: 'obj_shop_mirror', x: 280, y: 420 },
  { defId: 'obj_shop_hanger_stand', x: 1100, y: 480 },
];

export class ShopScene extends BaseScene {
  static readonly KEY = 'Shop';

  private balance = 0;
  private owned = new Set<string>();
  private slots: OutfitSlots = emptyOutfitSlots();
  private swimSlots: SwimSlots = emptySwimSlots();
  private filter: ShopFilter = 'all';

  private balanceText: Phaser.GameObjects.Text | null = null;
  private toast: Phaser.GameObjects.Text | null = null;
  private rack: RackView | null = null;
  private rackLayer: ObjectLayer | null = null;
  private shopLayer: ObjectLayer | null = null;
  private mannequin: AnimatedDogAvatar | null = null;
  private tryOnDog: AnimatedDogAvatar | null = null;
  private curtainClosed = false;
  private curtainVisual: Phaser.GameObjects.Rectangle | null = null;
  private confirmLayer: Phaser.GameObjects.Container | null = null;
  private promoItems: ItemDef[] = [];
  private buying = false;
  private unsubRack: (() => void) | null = null;
  private unsubShop: (() => void) | null = null;
  private receiptBusy = false;
  private persist: OutfitPersistController | null = null;

  constructor() {
    super(ShopScene.KEY);
  }

  preload(): void {
    this.preloadRealBg('bg_shop');
  }

  create(): void {
    this.cameras.main.setBackgroundColor(0xe6e0f8);
    this.addRealBg('bg_shop');

    // PNG z preload; gdy brak — proceduralne kształty (bez emoji-kwadratów)
    try {
      ensureShopTextures(this);
    } catch {
      /* headless */
    }

    const cache = OutfitStore.load();
    this.balance = cache.balanceZl;
    this.owned = new Set(cache.ownedItems);
    this.slots = cache.slots;
    this.swimSlots = cache.swimSlots;

    this.persist = createOutfitPersist({
      version: cache.version,
      onStatus: (m) => this.showToast(m),
    });

    this.paintShop();
    this.buildChrome();

    const promo = this.pickPromoOutfit();
    this.mannequin = new AnimatedDogAvatar({
      scene: this,
      x: 1080,
      y: 280,
      breed: cache.breed,
      color: cache.color,
      scale: 1.1,
      slots: promo.slots,
      swimSlots: promo.swimSlots,
      mode: 'normal',
      depth: 30,
    });
    this.add
      .text(1080, 200, 'Nowość ✨', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '16px',
        color: '#4a3f55',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(31);
    this.add
      .circle(1080, 280, 70, 0xffffff, 0.001)
      .setInteractive({ useHandCursor: true, hitArea: new Phaser.Geom.Circle(0, 0, 70) })
      .setDepth(32)
      .on('pointerdown', () => this.onMannequinTap());

    this.tryOnDog = new AnimatedDogAvatar({
      scene: this,
      x: 220,
      y: 400,
      breed: cache.breed,
      color: cache.color,
      scale: 1.35,
      slots: this.slots,
      swimSlots: this.swimSlots,
      mode: 'normal',
      depth: 40,
    });

    this.curtainVisual = this.add
      .rectangle(200, 300, 160, 220, 0xff9ec8, 0.55)
      .setStrokeStyle(3, 0xffffff)
      .setDepth(45);
    this.add
      .text(200, 200, 'Przymierzalnia', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '14px',
        color: '#4a3f55',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(46);

    // NPC sprzedawczyni — tekstura dog_*_idle (nie emoji)
    this.add
      .circle(960, 300, 40, 0xffd1dc)
      .setStrokeStyle(3, 0xffffff)
      .setDepth(20);
    const sellerKey = sellerDogTextureKey(this, cache.breed || 'maltese');
    if (sellerKey) {
      this.add
        .image(960, 296, sellerKey)
        .setDisplaySize(72, 72)
        .setDepth(21);
    } else {
      // headless / brak assetów — pastelowa sylwetka, bez emoji
      const g = this.add.graphics().setDepth(21);
      g.fillStyle(0xffe0b2, 1);
      g.fillCircle(960, 288, 22);
      g.fillStyle(0xffcc80, 1);
      g.fillEllipse(960, 318, 36, 28);
      g.fillStyle(0xffe0b2, 1);
      g.fillCircle(944, 276, 8);
      g.fillCircle(976, 276, 8);
    }
    this.add
      .text(960, 350, 'Sprzedawczyni', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '13px',
        color: '#4a3f55',
      })
      .setOrigin(0.5)
      .setDepth(21);
    this.add
      .circle(960, 300, 40, 0xffffff, 0.001)
      .setInteractive({ useHandCursor: true, hitArea: new Phaser.Geom.Circle(0, 0, 40) })
      .setDepth(22)
      .on('pointerdown', () => this.onSellerTap());

    this.shopLayer = new ObjectLayer({
      scene: this,
      defs: SHOP_OBJECT_DEFS,
      target: this.makeTarget(this.tryOnDog),
      interactRange: 100,
      groundBounds: {
        minX: 48,
        maxX: this.gameW - 48,
        minY: 360,
        maxY: this.gameH - 48,
      },
    });
    this.shopLayer.spawn(SHOP_PLACEMENTS);
    this.unsubShop = this.shopLayer.on((evt) => this.onShopObject(evt));

    this.rack = new RackView({
      scene: this,
      originX: 400,
      originY: 560,
      pageSize: 5,
      spacing: 108,
      depth: 55,
      onPageChange: () => this.rebuildRack(),
    });
    this.rebuildRack();

    this.events.once('shutdown', () => this.teardown());
    void this.refreshMe();
  }

  update(_t: number, dtMs: number): void {
    const dt = Math.min(0.05, dtMs / 1000);
    this.mannequin?.update(dt);
    this.tryOnDog?.update(dt);
    this.rackLayer?.update(dt);
    this.shopLayer?.update(dt);
    if (this.rackLayer && this.tryOnDog) {
      this.rackLayer.setSnapZones([
        { id: 'dog', x: this.tryOnDog.x, y: this.tryOnDog.y - 10, radius: 96 },
      ]);
    }
  }

  private paintShop(): void {
    this.add
      .rectangle(this.gameW / 2, 540, this.gameW, 240, 0xf5f0ff, 0.3)
      .setDepth(-50);
    this.add
      .rectangle(1000, 460, 220, 80, 0xd4a574, 0.7)
      .setStrokeStyle(3, 0xffffff)
      .setDepth(5);
    this.add
      .text(1000, 460, 'Kasa 🧾', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '18px',
        color: '#4a3f55',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(6);
  }

  private buildChrome(): void {
    this.add
      .text(this.gameW / 2, 28, 'Sklep z ubrankami', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '32px',
        color: '#4a3f55',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(200);

    this.add.circle(70, 36, 22, 0xffd700).setStrokeStyle(2, 0xffffff).setDepth(200);
    this.balanceText = this.add
      .text(100, 36, `${this.balance} zł`, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '22px',
        color: '#4a3f55',
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5)
      .setDepth(200);

    this.toast = this.add
      .text(this.gameW / 2, 70, '', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '18px',
        color: '#4a3f55',
        backgroundColor: '#fff8f0',
        padding: { x: 12, y: 6 },
      })
      .setOrigin(0.5)
      .setDepth(250)
      .setVisible(false);

    const filters: { id: ShopFilter; label: string; color: number }[] = [
      { id: 'all', label: 'Wszystko', color: 0xfff8f0 },
      { id: 'body', label: 'Ubranka', color: 0xff9ec8 },
      { id: 'hat', label: 'Czapki', color: 0x7ec8e3 },
      { id: 'bow', label: 'Kokardki', color: 0xff6b6b },
      { id: 'swim', label: 'Kąpiel', color: 0x5dade2 },
      { id: 'accessory', label: 'Dodatki', color: 0xb8e6d0 },
    ];
    filters.forEach((f, i) => {
      const x = 200 + i * 120;
      const y = 110;
      const bg = this.add
        .rectangle(x, y, 108, MIN_TOUCH, f.color)
        .setStrokeStyle(3, 0xffffff)
        .setInteractive({ useHandCursor: true })
        .setDepth(200);
      this.add
        .text(x, y, f.label, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '14px',
          color: '#4a3f55',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(201);
      bg.on('pointerdown', () => {
        this.filter = f.id;
        this.rack?.setPage(0);
        this.rebuildRack();
      });
    });

    UiKit.button(this, {
      x: 160,
      y: 680,
      label: 'Szafa',
      fill: 0xffb6c1,
      width: 160,
      height: MIN_TOUCH,
      name: 'open-wardrobe',
      onClick: () => getSceneRouter().go('Wardrobe'),
    }).setDepth(200);

    UiKit.button(this, {
      x: this.gameW - 180,
      y: 680,
      label: 'Wróć do miasteczka',
      fill: 0xffdab9,
      width: 240,
      height: MIN_TOUCH,
      name: 'back-to-town',
      onClick: () => getSceneRouter().go('Town'),
    }).setDepth(200);
  }

  private pickPromoOutfit(): { slots: OutfitSlots; swimSlots: SwimSlots } {
    const slots = emptyOutfitSlots();
    const swimSlots = emptySwimSlots();
    const unowned = ITEMS.filter((i) => !this.owned.has(i.id));
    const pick = (unowned.length ? unowned : ITEMS).slice(0, 4);
    this.promoItems = pick;
    for (const item of pick) {
      const r = applyEquip(item, slots, swimSlots, 'force');
      Object.assign(slots, r.slots);
      Object.assign(swimSlots, r.swimSlots);
    }
    return { slots, swimSlots };
  }

  /** Klik na manekina — pokaż promowaną rzecz do kupienia (był tylko dekoracją). */
  private onMannequinTap(): void {
    playFx(this, 'sparkle', 1080, 280);
    void this.mannequin?.playAction('happy');
    const promo = this.promoItems.find((i) => !this.owned.has(i.id));
    if (promo) {
      this.showToast(`Nowość: ${promo.namePl}! ✨`);
      this.openConfirm(promo);
      return;
    }
    this.showToast('Masz już całą nową kolekcję! 🎉');
  }

  /** Klik na sprzedawczynię — powitanie (była całkiem martwa). */
  private onSellerTap(): void {
    playFx(this, 'hearts', 960, 300);
    this.showToast('Hau! Witaj w sklepie! 🐶🛍️');
    void this.tryOnDog?.playAction('wave');
  }

  private makeTarget(av: AnimatedDogAvatar): InteractTarget {
    return {
      moveTo: (x, y) => av.moveTo(x, y),
      holdItem: (k) => av.holdItem(k),
      playAction: (a) => av.playAction(a as 'happy'),
      getPosition: () => ({ x: av.x, y: av.y }),
      get x() {
        return av.x;
      },
      get y() {
        return av.y;
      },
      get isMoving() {
        return av.isMoving;
      },
    };
  }

  private rebuildRack(): void {
    if (!this.rack || !this.tryOnDog) return;
    this.unsubRack?.();
    this.unsubRack = null;
    this.rackLayer?.destroy();
    this.rackLayer = null;

    const catalog = shopCatalog(this.filter);
    let result: ReturnType<RackView['compute']>;

    if (this.filter === 'all') {
      result = this.rack.computeFromItems(
        catalog,
        this.owned,
        this.slots,
        this.swimSlots,
      );
    } else {
      const tab: RackTabId =
        this.filter === 'swim' ? 'swim' : (this.filter as RackTabId);
      result = this.rack.compute(
        tab,
        this.owned,
        this.slots,
        this.swimSlots,
        ITEMS,
      );
    }

    const { views, layout, defs } = result;
    this.rackLayer = new ObjectLayer({
      scene: this,
      defs,
      target: this.makeTarget(this.tryOnDog),
      interactRange: 110,
      groundBounds: {
        minX: 48,
        maxX: this.gameW - 48,
        minY: 360,
        maxY: this.gameH - 48,
      },
    });
    this.rackLayer.setSnapZones([
      { id: 'dog', x: this.tryOnDog.x, y: this.tryOnDog.y - 10, radius: 96 },
    ]);
    this.rackLayer.spawn(layout.placements);
    this.rack.paintOverlays(views, layout);
    this.unsubRack = this.rackLayer.on((evt) => this.onRackEvent(evt));
  }

  private onRackEvent(evt: InteractEvent): void {
    const itemId = itemIdFromRackDef(evt.defId);
    if (!itemId) return;
    const item = ITEMS.find((i) => i.id === itemId);
    if (!item) return;

    if (evt.type === 'tap') {
      if (this.owned.has(item.id)) this.tryOn(item);
      else this.openConfirm(item);
      return;
    }
    if (evt.type === 'snap' && evt.snapZoneId === 'dog') {
      if (this.owned.has(item.id)) this.tryOn(item);
      else this.openConfirm(item);
      this.returnHanger(evt.objectId);
      return;
    }
    if (evt.type === 'drag-end' && this.tryOnDog) {
      if (hitDogForEquip(evt.x, evt.y, this.tryOnDog.x, this.tryOnDog.y, 100)) {
        if (this.owned.has(item.id)) this.tryOn(item);
        else this.openConfirm(item);
      }
      this.returnHanger(evt.objectId);
    }
  }

  private returnHanger(objectId: string): void {
    const obj = this.rackLayer?.getObject(objectId);
    if (!obj || !this.rack?.layout) return;
    const place = this.rack.layout.placements.find((p) => p.defId === obj.def.id);
    if (place) {
      obj.setPosition(place.x, place.y);
      obj.container.setVisible(true);
    }
  }

  private tryOn(item: ItemDef): void {
    const next = applyEquip(item, this.slots, this.swimSlots, 'force');
    this.slots = next.slots;
    this.swimSlots = next.swimSlots;
    this.tryOnDog?.setOutfit(this.slots, this.swimSlots);
    if (next.mode === 'swim') this.tryOnDog?.setMode('swim');
    else this.tryOnDog?.setMode('normal');
    // lustro lokalne + PUT /api/profile/outfit (debounce) — jak w szafie
    this.persist?.schedule(this.slots, this.swimSlots);
    if (this.tryOnDog) {
      this.tryOnDog.holdItem(itemTextureKey(item.id));
      playFx(this, 'sparkle', this.tryOnDog.x, this.tryOnDog.y - 30);
      void this.tryOnDog.playAction('happy').then(() => {
        this.tryOnDog?.holdItem(null);
      });
    }
    this.showToast(`Przymierzone: ${item.namePl} ✨`);
    this.rebuildRack();

    // W9-B: krok misji shop_try_on
    const mission = shopMissionFromTryOn(item.id);
    if (mission) {
      this.game.events.emit(INTERACT_BUS_EVENT, mission);
      this.events.emit('quest-progress', mission);
    }
  }

  private onShopObject(evt: InteractEvent): void {
    if (
      evt.defId === 'obj_shop_curtain' &&
      (evt.type === 'toggle' || evt.type === 'tap')
    ) {
      this.curtainClosed = !this.curtainClosed;
      if (this.curtainVisual) {
        this.curtainVisual.setFillStyle(
          this.curtainClosed ? 0xc2185b : 0xff9ec8,
          this.curtainClosed ? 0.9 : 0.55,
        );
        this.curtainVisual.setScale(this.curtainClosed ? 1.05 : 1, 1);
      }
      if (this.tryOnDog) {
        this.tryOnDog.container.setAlpha(this.curtainClosed ? 0.15 : 1);
      }
      this.showToast(this.curtainClosed ? 'Kotara zasunięta' : 'Kotara odsunięta');
      return;
    }
    if (evt.defId === 'obj_shop_register' && evt.type === 'tap') {
      this.printReceipt();
      return;
    }
    if (evt.defId === 'obj_shop_bell' && evt.type === 'tap') {
      playFx(this, 'notes', evt.x, evt.y);
      this.showToast('Ding! 🔔');
      void this.tryOnDog?.playAction('wave');
    }
    if (evt.defId === 'obj_shop_mirror' && evt.type === 'tap') {
      this.showToast('Ale ładnie wyglądasz! 💖');
      return;
    }
    if (evt.defId === 'obj_shop_hanger_stand' && evt.type === 'tap') {
      this.showToast('Wieszak pełen ubranek! 👕');
      return;
    }
    if (evt.defId === 'obj_shop_bag' && evt.type === 'pickup') {
      playFx(this, 'stars', evt.x, evt.y);
      this.showToast('Torebka na zakupy! 🛍️');
    }
  }

  private printReceipt(): void {
    if (this.receiptBusy) return;
    this.receiptBusy = true;
    playFx(this, 'notes', 1000, 420);
    const paper = this.add
      .rectangle(1000, 400, 80, 20, 0xfffef5)
      .setStrokeStyle(2, 0xcccccc)
      .setDepth(100);
    const txt = this.add
      .text(1000, 400, 'paragon', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '12px',
        color: '#4a3f55',
      })
      .setOrigin(0.5)
      .setDepth(101);
    this.tweens.add({
      targets: [paper, txt],
      y: 520,
      scaleY: 2.2,
      duration: 600,
      ease: 'Quad.Out',
      onComplete: () => {
        this.tweens.add({
          targets: [paper, txt],
          alpha: 0,
          duration: 400,
          delay: 400,
          onComplete: () => {
            paper.destroy();
            txt.destroy();
            this.receiptBusy = false;
          },
        });
      },
    });
    this.showToast('Paragon wydrukowany 🧾');
  }

  private openConfirm(item: ItemDef): void {
    this.closeConfirm();
    const dim = this.add
      .rectangle(
        this.gameW / 2,
        this.gameH / 2,
        this.gameW,
        this.gameH,
        0x4a3f55,
        0.45,
      )
      .setInteractive()
      .setDepth(300);
    const panel = this.add
      .rectangle(0, 0, 420, 280, 0xfff8f0)
      .setStrokeStyle(4, 0xffb6c1);
    const name = this.add
      .text(0, -80, item.namePl, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '26px',
        color: '#4a3f55',
        fontStyle: 'bold',
        align: 'center',
        wordWrap: { width: 380 },
      })
      .setOrigin(0.5);
    const price = this.add
      .text(0, -30, `${item.priceZl} zł`, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '32px',
        color: '#4a3f55',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    const afford =
      this.balance >= item.priceZl
        ? `Saldo: ${this.balance} zł`
        : `Brakuje ${item.priceZl - this.balance} zł`;
    const hint = this.add
      .text(0, 10, afford, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '18px',
        color: this.balance >= item.priceZl ? '#6b5f7a' : '#c0392b',
      })
      .setOrigin(0.5);
    const root = this.add.container(this.gameW / 2, this.gameH / 2, [
      panel,
      name,
      price,
      hint,
    ]);
    root.setDepth(301);

    const yes = UiKit.button(this, {
      x: this.gameW / 2 - 100,
      y: this.gameH / 2 + 80,
      label: `Kup za ${item.priceZl} zł`,
      fill: 0xb8e6d0,
      width: 200,
      height: MIN_TOUCH + 8,
      onClick: () => {
        this.closeConfirm();
        void this.buy(item);
      },
    });
    yes.setDepth(302);
    const no = UiKit.button(this, {
      x: this.gameW / 2 + 120,
      y: this.gameH / 2 + 80,
      label: 'Nie',
      fill: 0xffb6c1,
      width: 120,
      height: MIN_TOUCH + 8,
      onClick: () => this.closeConfirm(),
    });
    no.setDepth(302);
    this.confirmLayer = this.add.container(0, 0, [dim, root, yes, no]);
    this.confirmLayer.setDepth(300);
  }

  private closeConfirm(): void {
    this.confirmLayer?.destroy(true);
    this.confirmLayer = null;
  }

  private async buy(item: ItemDef): Promise<void> {
    if (this.buying) return;
    this.buying = true;
    this.showToast('Kupuję…');
    const res = await postPurchase({
      kind: 'item',
      id: item.id,
      qty: 1,
      idempotencyKey: newIdempotencyKey(),
    });
    this.buying = false;

    if (res.ok) {
      this.balance = res.data.balance;
      if (res.data.ownedItems) {
        this.owned = new Set(res.data.ownedItems);
      } else {
        this.owned.add(item.id);
      }
      OutfitStore.commitPurchase(
        this.balance,
        res.data.ownedItems ?? [...this.owned],
      );
      this.balanceText?.setText(`${this.balance} zł`);
      this.printReceipt();
      this.tryOn(item);
      this.showToast('Kupione! 🎉');
      return;
    }
    // Rozróżnione komunikaty: salda / logowania / sieci (po polsku z diakrytykami)
    if (res.code === 'INSUFFICIENT_FUNDS') {
      this.showToast('Za mało złotówek — zbierz więcej w minigrach!');
      return;
    }
    if (res.code === 'UNAUTHORIZED') {
      this.showToast('Musisz być zalogowany, aby kupować.');
      return;
    }
    if (res.status === 0) {
      this.showToast('Brak połączenia z siecią — spróbuj później.');
      return;
    }
    this.showToast('Coś poszło nie tak — spróbuj później.');
  }

  private async refreshMe(): Promise<void> {
    const me = await fetchMe();
    if (!me.ok) return;
    this.balance = me.data.balanceZl;
    OutfitStore.saveBalance(this.balance);
    OutfitStore.saveDog({
      breed: me.data.breed,
      color: me.data.color,
      dogName: me.data.dogName,
    });
    this.balanceText?.setText(`${this.balance} zł`);
    this.tryOnDog?.setBreed(me.data.breed, me.data.color);
    this.mannequin?.setBreed(me.data.breed, me.data.color);
  }

  private showToast(msg: string): void {
    if (!this.toast) return;
    this.toast.setText(msg);
    this.toast.setVisible(true);
    this.time.delayedCall(2200, () => this.toast?.setVisible(false));
  }

  private teardown(): void {
    this.unsubRack?.();
    this.unsubShop?.();
    // G7: natychmiastowy PUT stroju przed anulowaniem debounced timera
    disposeShopOutfitPersist(this.persist);
    this.persist = null;
    this.rackLayer?.destroy();
    this.shopLayer?.destroy();
    this.rack?.destroy();
    this.mannequin?.destroy();
    this.tryOnDog?.destroy();
    this.closeConfirm();
    this.rackLayer = null;
    this.shopLayer = null;
    this.rack = null;
    this.mannequin = null;
    this.tryOnDog = null;
  }
}

/**
 * Teardown persist stroju (G7): flush() → PUT (w tym follow-up dirty), potem destroy().
 * Wyjście ze sceny przed upływem debounce nie gubi zapisu serwerowego.
 */
export function disposeShopOutfitPersist(
  persist:
    | Pick<OutfitPersistController, 'flush' | 'destroy'>
    | null
    | undefined,
): void {
  disposeOutfitPersist(persist);
}

export default ShopScene;
